import { Component, OnInit, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { ReviewsService } from 'src/app/service/reviews.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { Submission, Review } from 'src/app/model/paper';
import { SubmissionStatus } from 'src/app/model/paper';
import { ReviewStatus } from 'src/app/enum/review.status';
import { InfoService } from 'src/app/service/info.service';
import { User } from 'src/app/model/user';
import { NotificationService } from 'src/app/service/notification.service';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { EventsService } from 'src/app/service/events.service';
import { FormField, SortedFormFields } from '../../model/form.field';
import { FormFieldChoice  } from '../../model/form.field.choice';
import { Answer } from '../../model/answer';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from 'src/app/service/admin.service';
import { EditEmailComponent } from 'src/app/component/edit-email/edit-email.component';

export type PaperReviewListViewMode = 'full' | 'show-all-submissions';

@Component({
  selector: 'app-paper-review-list',
  templateUrl: './paper-review-list.component.html',
  styleUrls: ['./paper-review-list.component.scss']
})
export class PaperReviewListComponent implements OnInit, OnChanges {
  @Input() public element: Submission | Review;

  public reviewConfig: EventReviewConfiguration;

  public submission: Submission;
  public review: Review;

  @Input() public viewMode: PaperReviewListViewMode = 'full';

  @Input() public userIsChair = false;

  // Override opcional da "visão do autor" (esconde revisores + label "Revisor:").
  // Quando NULL (default), o componente decide sozinho (isAuthor && !userIsChair).
  // O paper-info passa `effectiveAuthor` p/ o preview de perfil funcionar (chair
  // vendo como autor também esconde os revisores).
  @Input() public authorView: boolean = null;

  // Oculta a IDENTIDADE do revisor (nome → "Revisor N" anonimizado), mantendo o
  // conteúdo/status da revisão visível. Usado no preview/visão de Coordenador de
  // sessão, que lê as revisões mas não pode saber quem revisou.
  @Input() public hideReviewerName = false;

  // Modo de visualização do CORPO da revisão — dois eixos independentes (scores ×
  // textos), via classes `rb-scores-sxs` / `rb-texts-sxs` no `.review-body`:
  //   'stacked'       → scores e textos EMPILHADOS (largura cheia) — default;
  //   'mixed'         → scores LADO A LADO (grade em caixas), textos empilhados;
  //   'side-by-side'  → scores e textos LADO A LADO (ambos em caixas/colunas).
  @Input() public layout: 'stacked' | 'mixed' | 'side-by-side' = 'stacked';

  public reviews: Array<Review>;

  // Lista de EXIBIÇÃO (derivada de `reviews`, nunca destrutiva). Chair e demais
  // perfis veem todos os cards; só o autor de submissão decidida vê o subconjunto.
  public displayedReviews: Array<Review> = [];

  public eventId: number;

  public authorViewMode = true;

  public usedStatus: ReviewStatus[];

  @Output() public requestReviewRefresh: EventEmitter<{reviewId: number, delete?: boolean }> = new EventEmitter();

  // Avisa o pai se HÁ pelo menos um card expansível (revisão respondida). O
  // botão "expandir/colapsar todos" vive no título da seção (paper-info) e só
  // faz sentido quando há algo a expandir. Emitido a cada recomputo dos cards.
  @Output() public expandableStateChanged: EventEmitter<boolean> = new EventEmitter();

  // Um card é expansível quando a revisão foi respondida (mesma regra do
  // [disabled] do chevron e do *ngIf do corpo).
  public get hasExpandableReview(): boolean {
    return (this.displayedReviews || []).some(r => r.wasAnswered());
  }

  constructor(
    private reviewService: ReviewsService,
    private dialog: MatDialog,
    private infoService: InfoService,
    private reviewConfigurationService: ReviewsConfigurationService,
    private notificationService: NotificationService,
    private eventsService: EventsService,
    private activatedRoute: ActivatedRoute,
    private adminService: AdminService
  ) { }

  ngOnInit(){
    this.load();
  }

  load() {
    let isReviewID = false;

    if (this.element instanceof Submission) {
      this.submission = this.element;
      if (this.submission.reviews && this.submission.reviews.length > 0) {
        // Test if submission.reviews has review ID. Else, it has Reviews.
        isReviewID = !this.submission.reviews.some(isNaN);
      }
      if (!isReviewID) {
        this.reviews = <Array<Review>> this.submission.reviews;
      }

      if (!this.reviews) {
        // Get reviews (gives the function the hidereview flag based whether it is on the admin route or not)
        this.reviewService.getReviewsBySubmissionId(this.submission.id, !this.isAdminRoute()).subscribe(reviews => {
          this.submission.reviews = reviews.map(r => {
            r.submission = this.submission;
            return r;
          });

          this.reviews = <Review[]> this.submission.reviews;
          this.defineEvent(this.submission);
          this.reviews.forEach(r => r.reviewQuestionVisibility = r.wasAnswered());
          this.elementsDefinedInit();
        });
      } else {
        this.defineEvent(this.submission);
        this.reviews.forEach(r => r.reviewQuestionVisibility = r.wasAnswered());
        this.elementsDefinedInit();
      }
    } else {
      this.review = this.element;
      this.reviews = [this.review];

      this.defineEvent(this.review.submission);
      this.reviews.forEach(r => r.reviewQuestionVisibility = r.wasAnswered());
    }

    setTimeout(() => {
      if (this.element instanceof Submission) {
        this.defineEvent(this.submission);
      }else{
        this.defineEvent(this.review.submission);
      }
      this.elementsDefinedInit();
      this.reviewConfigurationService.getReviewsConfiguration(this.eventId).subscribe(reviewConfig => {
        this.reviewConfig = reviewConfig;
        // `authorsCanView` pode alterar a liberação → recomputa os cards exibidos.
        this.applyDisplayedReviews();
      });
    });
  }

  ngOnChanges(changed) {
    if (changed.element) {
      this.submission = undefined;
      this.review = undefined;
      this.reviews = undefined;

      this.load();
    }

    // `authorView`/`hideReviewerName` também alimentam a decisão de visão-autor e
    // de identidade. Ao trocar o perfil de preview de revisor/coord.sessão → autor,
    // só `authorView` muda (userIsChair segue false), então precisa recalcular aqui
    // ou o `authorViewMode` fica stale e o autor acaba vendo o cabeçalho não-autor.
    if (changed.userIsChair || changed.authorView || changed.hideReviewerName) {
      this.elementsDefinedInit();
    }
  }

  private isAdminRoute(): boolean {
    return this.adminService.isEventAdminRoute(this.activatedRoute.snapshot); 
  }

  public defineEvent(submission: Submission): void {
    this.eventsService.getEventByTrack(submission.trackID)
      .subscribe(e => this.eventId = e.id);
  }

  public elementsDefinedInit() {
    if (this.viewMode === 'show-all-submissions') {
      this.reviews.forEach(r => r.reviewQuestionVisibility = true);
    }

    if (this.authorView !== null && this.authorView !== undefined) {
      // Forçado pelo pai (preview de perfil): esconde revisores quando visão-autor.
      this.authorViewMode = this.authorView;
    } else {
      this.authorViewMode = this.submission ? this.submission.isAuthor(this.infoService.user.id) : false;
      if (this.authorViewMode && this.userIsChair) {
        this.authorViewMode = false;
      }
    }

    // Filtro de EXIBIÇÃO, não destrutivo: na visão-autor de submissão já decidida,
    // o autor só vê revisões concluídas. `this.reviews` permanece completo — chair
    // e demais perfis veem TODOS os cards (assigned, reminded, declined, late…).
    this.applyDisplayedReviews();

    this.defineUsedStatus();
  }

  // Autores veem revisões só se o checkbox "Autores podem ver revisões"
  // (authorsCanView, página Configurações) estiver marcado. Simples leitura da config.
  public get authorsCanViewReviews(): boolean {
    return !!this.reviewConfig?.authorsCanView;
  }

  // Recomputa os cards exibidos e avisa o pai se algum é expansível. Único ponto
  // que atribui displayedReviews (assim o emit nunca fica dessincronizado).
  private applyDisplayedReviews(): void {
    this.displayedReviews = this.computeDisplayedReviews();
    this.expandableStateChanged.emit(this.hasExpandableReview);
  }

  private computeDisplayedReviews(): Review[] {
    const all = this.reviews || [];
    if (this.authorViewMode) {
      if (!this.authorsCanViewReviews) { return []; }   // config desmarcada → nenhum card
      return all.filter(r => r.status === ReviewStatus.COMPLETED);
    }
    return all;
  }

  public defineUsedStatus() {
    if (this.reviews?.length > 0) {
      this.usedStatus = [...new Set(this.reviews.map(e => e.status))];
    }
  }

  public delete(review: Review): void {
    const id = review.id;

    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'messages.confirm-removed-review',
        content: ''
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.reviewService.deleteReview(id).subscribe(() => {
          this.notificationService.notify('messages.removed-user-review', { params: { user: review.userId } });
          this.reviews = this.reviews.filter(r => r.id !== id);

          this.requestReviewRefresh.emit({ reviewId: id, delete: true });
        });
      }
    });
  }

  public toggleReviewQuestions(review: Review): void {
    review.reviewQuestionVisibility = !review.reviewQuestionVisibility;
  }

  // Abre/fecha o corpo de TODAS as revisões respondidas de uma vez (controle na
  // linha do título da seção, no paper-info).
  public setAllReviewsExpanded(expanded: boolean): void {
    (this.reviews || []).forEach(r => {
      if (r.wasAnswered()) { r.reviewQuestionVisibility = expanded; }
    });
  }

  // Ícone (fonte legada jems3s/jems3r) do status da revisão — mesma fonte de
  // verdade usada em Minhas revisões (app-paper-status / review-card-new).
  public reviewStatusIcon(review: Review) {
    return ReviewStatus.icon(review.status);
  }

  // Este espectador pode ver a IDENTIDADE dos revisores (não a própria)?
  //  - Chair: sempre.
  //  - Coord. de sessão (hideReviewerName): nunca.
  //  - Revisor (demais): só quando a config de revisão libera
  //    (tpcViewReviewIdentity === 'ALL') — mesma regra da Discussão.
  public get canViewReviewerIdentities(): boolean {
    if (this.authorViewMode) { return false; }   // autor nunca vê identidade (vê "Revisão N")
    if (this.userIsChair) { return true; }
    if (this.hideReviewerName) { return false; }
    return this.reviewConfig?.tpcViewReviewIdentity === 'ALL';
  }

  // Mostrar o nome do revisor DESTA revisão? Sempre identificável: (a) a revisão
  // que o próprio usuário ESCREVEU (é ele) e (b) a revisão que ELE DELEGOU a alguém
  // (o delegado deve aparecer p/ o delegante). As demais seguem `canViewReviewerIdentities`.
  // NÃO uso `userIsReviewer` aqui: ele também casa `assignedBy === eu` das revisões
  // que atribuí como CHAIR (isFirstAssign=true), o que faria o chair ver todos os
  // nomes na interface de revisor. A delegação se distingue por `isFirstAssign===false`.
  public showReviewerIdentity(review: Review): boolean {
    return this.canViewReviewerIdentities || this.isOwnReview(review) || this.isDelegatedByMe(review);
  }

  private isOwnReview(review: Review): boolean {
    const me = this.infoService.user.id;
    return review.user instanceof User ? review.user.id === me : review.user === me;
  }

  // Revisão que ESTE usuário delegou: quem delegou fica em `assignedBy` e a
  // delegação (≠ primeira atribuição do chair) tem `isFirstAssign === false`.
  private isDelegatedByMe(review: Review): boolean {
    return review.isFirstAssign === false && review.assignedByUserId === this.infoService.user.id;
  }

  public userIsReviewer(review: Review): boolean {
    const currentUserId = this.infoService.user.id;
    let isReviewer = false;

    if (review.user instanceof User) {
      isReviewer = (review.user.id === currentUserId);
    }else{
      isReviewer = (review.user === currentUserId);
    }
    if (isReviewer == false){
      if (review.assignedBy instanceof User) {
        isReviewer = (review.assignedBy.id === currentUserId);
      }else{
        isReviewer = (review.assignedBy === currentUserId);
      }
    }
    return isReviewer;
  }

  public notify(review: Review) {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'messages.confirm-notify-review',
        content: ''
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }

      this.reviewService.sendInvitationEmail([ review.id ]).subscribe(() => {
        this.notificationService.notify('reviews.notify', { params: { review: review.id }});

        this.requestReviewRefresh.emit({ reviewId: review.id });
      });
    });

    //     const dialogConfig = new MatDialogConfig();
        
    //     dialogConfig.disableClose = false;
    //     dialogConfig.autoFocus = false;
    //     dialogConfig.width = '600px';

    //     // Get email infos for showing it to user (TODO: trocar por backend call)
    //     const emailTitle= 'TEst'
    //     const emailContent = `
    //     Dear [Recipient's Name],
        
    // I hope this message finds you well. I wanted to provide an update on the progress of Project Alpha and share some important details regarding our next steps. Over the past month, our team has worked diligently to meet key milestones, including the successful completion of the initial design phase. This achievement sets a strong foundation for the development stage, which is scheduled to begin next week.

    // As we move forward, it's crucial to address a few potential challenges. One of the key concerns is ensuring seamless integration between the existing system and the new features being developed. To mitigate risks, we’ve allocated additional resources for testing and quality assurance. Additionally, the team has planned regular review sessions to ensure that all stakeholders are aligned and any issues are addressed promptly.

    // Lastly, I want to thank you for your continued support and collaboration. Your insights have been invaluable in shaping the direction of this project. Please don’t hesitate to reach out if you have any questions or suggestions. I’ll be scheduling a follow-up meeting next week to discuss progress and gather your feedback.

    // Best regards,
    // [Your Name]
    // [Your Position]
    // [Your Contact Information]`

    //     dialogConfig.data = {
    //       emailTitle:emailTitle,
    //       emailBody: emailContent}

    //     // Create dialog to user change the email infos before notifying
    //     const dialogRef = this.dialog.open(EditEmailComponent, dialogConfig);
    //     dialogRef.afterClosed().subscribe(emailInfo => {
    //       if(emailInfo){
    //         console.log(emailInfo.emailTitle, emailInfo.emailBody)

    //         // Send email for reviewer (informing the title)
    //         this.reviewService.sendInvitationEmail([ review.id ]).subscribe(() => {
    //           this.notificationService.notify('reviews.notify', { params: { review: review.id}});
      
    //           this.requestReviewRefresh.emit({ reviewId: review.id });
    //         });
    //       }
    //     });
  }

  public updateReview(review: Review): void {
    this.requestReviewRefresh.emit({ reviewId: review.id });
  }

  public visibleFields(fields: (FormField[] | SortedFormFields[])): FormField[] | SortedFormFields[] {
    if (this.authorViewMode) {
      if (fields[0] instanceof FormField) {
        // Fields is FormField[]
        return (<FormField[]>fields).filter(f => !f.hidden);
      }

      // Fields is SortedFormFields[]
      const sortedFields = (<SortedFormFields[]>fields).map(sf => {
        sf.fields = sf.fields.filter(f => !f.hidden);
        return sf;
      });

      return sortedFields?.length > 0 ? sortedFields : [];
    }
    return fields;
  }

  // Change the selected choice's background
  // Retorna o mapa de classe p/ [ngClass]: a opção escolhida pelo revisor recebe
  // `is-selected` (estilizada via SCSS, theme-aware), no lugar do estilo inline.
  public classAnswer(choice: FormFieldChoice, answerForReview: FormFieldChoice[]) {
    return { 'is-selected': !!answerForReview.find(a => a.id == choice.id) };
  }
}
